/**
 * Cell-renderer + placeholder components for the Staff EntityTable/Detail
 * config.
 *
 * Extracted from staffEntityConfig.tsx so that file's only exports are
 * config objects/functions (react-refresh/only-export-components — Fast
 * Refresh needs a file to export either only components or only
 * non-components, not a mix).
 */

import React from 'react';
import { Avatar } from '@/components/common/Avatar';
import { Badge } from '@/components/common/Badge';
import type { StaffResponse } from '@features/people/api/staff';
import { getFullName } from '@/utils';

export const StaffNameCell: React.FC<{ staff: StaffResponse }> = ({ staff }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <Avatar
      firstName={staff.first_name}
      lastName={staff.last_name}
      src={staff.photo_url ?? undefined}
      size="sm"
    />
    <div>
      <div style={{ fontWeight: 500, lineHeight: 1.3 }}>
        {getFullName(staff.first_name, staff.last_name)}
      </div>
      <div style={{ fontSize: 12, color: '#64748b' }}>{staff.email}</div>
    </div>
  </div>
);

const roleColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  owner: 'primary',
  admin: 'primary',
  manager: 'secondary',
  staff: 'success',
};

export const RoleCell: React.FC<{ staff: StaffResponse }> = ({ staff }) => (
  <Badge variant={roleColors[staff.role] ?? 'default'} size="sm">
    {staff.role.replace('_', ' ')}
  </Badge>
);

export const StaffTypeCell: React.FC<{ staff: StaffResponse }> = ({ staff }) =>
  staff.staff_type ? (
    <Badge variant="default" size="sm">{staff.staff_type}</Badge>
  ) : (
    <span style={{ color: '#94a3b8' }}>—</span>
  );

// Deliberately NOT typed as React.FC — the `render` slot it fills
// (EntityDetailConfig's TabConfig) expects `(entityId: string) => ReactNode`,
// and React.FC's return type (ReactNode | Promise<ReactNode>) is too wide to
// satisfy that. A plain inferred `() => JSX.Element` matches structurally
// (extra unused params are fine).
export const NotWiredPlaceholder = () => <p style={{ color: '#64748b' }}>Not wired yet.</p>;
